'use client'

import { animateTopBar } from '@/animations/nav/animateNav'
import { useGSAPContext } from '@/hooks'
import { useStoreI18n, useStoreNavigation } from '@/store'
import { Typo3Header } from '@/types'
import { BaseLink, Button, ButtonIcon, Logo, Popover } from '@/ui/components'
import dynamic from 'next/dynamic'
import { usePathname } from 'next/navigation'
import {
  memo,
  Suspense,
  useEffect,
  useEffectEvent,
  useRef,
  useState
} from 'react'

const SearchInput = dynamic(() => import('./SearchInput'), {
  ssr: false
})

interface Props {
  data: Typo3Header
}

const TopBar = ({ data }: Props) => {
  const comp = useRef(null)
  const tl = useRef<GSAPTimeline | null>(null)
  const locale = useStoreI18n.use.currentLocale()
  const [isCompSearchModalOpen, setCompIsSearchModalOpen] = useState(false)

  const { setIsNavOpen, setIsSearchModalOpen, isSearchModalOpen, isNavOpen } =
    useStoreNavigation()
  const pathName = usePathname()
  const buttons = [data?.shopButton, data?.liveStatusButton]

  const onIsSearchModalOpen = useEffectEvent(() => {
    if (isSearchModalOpen) {
      setIsNavOpen(false)
    }
  })

  useEffect(() => {
    onIsSearchModalOpen()
  }, [isSearchModalOpen])

  const openSearchModal = () => {
    setIsSearchModalOpen(true)
  }

  useGSAPContext({
    scope: comp,
    respectReducedMotion: false,
    callback() {
      animateTopBar(comp, tl)
    }
  })

  const onClickButton = () => {
    if (isCompSearchModalOpen) {
      setIsSearchModalOpen(false)
    } else {
      setIsNavOpen(!isNavOpen)
    }
  }

  const handleSetCompIsSearchModalOpen = useEffectEvent(() => {
    setCompIsSearchModalOpen(isSearchModalOpen)
  })

  useEffect(() => {
    if (!tl.current) {
      return
    }

    handleSetCompIsSearchModalOpen()

    if (isNavOpen || isSearchModalOpen) {
      tl.current?.timeScale(1).play()
    } else {
      setTimeout(() => {
        tl.current?.timeScale(2).reverse()
      }, 600)
    }
  }, [isNavOpen, isSearchModalOpen])

  return (
    <div ref={comp}>
      <div className="relative z-100 overflow-hidden">
        <div className="relative z-120 flex justify-between">
          <div className="gap-2xs p-xs flex items-center">
            <Button
              icon={
                isNavOpen || isSearchModalOpen
                  ? 'material-symbols:close'
                  : 'material-symbols:menu'
              }
              iconPosition="left"
              data-is-open={isNavOpen || isSearchModalOpen}
              onClick={() => onClickButton()}
              className="max-sm:px-sm"
            >
              <span className="relative block overflow-hidden">
                <span className="relative block transition-transform group-data-[is-open='true']/button:-translate-y-full">
                  Menu
                </span>
                <span className="absolute inset-0 block transition-transform group-data-[is-open='false']/button:translate-y-full">
                  Close
                </span>
              </span>
            </Button>

            {buttons.map((button, buttonIndex) => (
              <div
                className="max-md:hidden"
                key={`nav-button-${buttonIndex.toString()}`}
                data-topbar-button
              >
                {button?.menu && button?.menu?.length > 0 && (
                  <>
                    {button?.menu?.length > 1 ? (
                      <Popover
                        className="max-w-56 p-0"
                        button={<Button {...button} />}
                      >
                        <div className="flex flex-col">
                          {button?.menu?.map((menuItem, index) => (
                            <Button
                              key={`nav-button-${buttonIndex.toString()}-${index.toString()}`}
                              variant="secondary"
                              icon={menuItem.icon}
                              iconPosition="left"
                              size="m"
                              href={menuItem.href}
                              target={menuItem.target}
                              className="justify-start"
                            >
                              {menuItem.linkText}
                            </Button>
                          ))}
                        </div>
                      </Popover>
                    ) : (
                      <Button
                        {...button}
                        href={button?.menu[0]?.href as string}
                        target={button?.menu[0]?.target as string}
                      />
                    )}
                  </>
                )}
              </div>
            ))}

            <div data-topbar-button>
              <ButtonIcon
                className="max-lg:hidden"
                icon="material-symbols:search"
                aria-label="Search"
                onClick={() => openSearchModal()}
              />
            </div>

            <div
              className="top-xs right-xs absolute left-26 lg:hidden"
              data-search-input-mobile
            >
              <Suspense>
                <SearchInput data={data?.search} variant="mobile" />
              </Suspense>
            </div>
          </div>
          <BaseLink
            href={pathName === `/${locale}` ? undefined : `/${locale}`}
            aria-label={pathName === `/${locale}` ? undefined : 'Home'}
            nextLink={pathName !== `/${locale}`}
            as={pathName === `/${locale}` ? 'div' : `a`}
            isTransition={pathName !== `/${locale}`}
            className="px-md flex items-center bg-neutral-100"
            onClick={() => setIsNavOpen(false)}
          >
            <Logo />
          </BaseLink>
        </div>
        <div
          className="pointer-events-none absolute inset-0 right-full bg-white"
          data-topbar-bg
        />
      </div>
      <div
        className="bg-alpha-white-300 fixed inset-0 hidden backdrop-blur-xl"
        data-nav-overlay
        onClick={() => {
          if (isCompSearchModalOpen) {
            setIsSearchModalOpen(false)
          } else {
            setIsNavOpen(false)
          }
        }}
      />
    </div>
  )
}

export default memo(TopBar)
